import {
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { UUID } from '~/types/common';

import { VestigasHTTPError } from '@vestigas/data';

import { queryKeysSupplier } from '../queryKeys';

import { fetchSuppliersPriority } from './fetchSuppliersPriority';
import { type SuppliersPriorityResponse } from './types';

/**
 * Generates the query options for the suppliers priority query.
 *
 * @param recipientCompanyId - Recipient company ID.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and staleTime.
 */
export const getSuppliersPriorityQueryOptions = <
  TData = SuppliersPriorityResponse,
>(
  recipientCompanyId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<SuppliersPriorityResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  queryOptions({
    gcTime: ms('5m'),
    queryFn: recipientCompanyId
      ? async ({ signal }) =>
          fetchSuppliersPriority(recipientCompanyId, { signal })
      : skipToken,
    queryKey: queryKeysSupplier.getPriority(recipientCompanyId),
    staleTime: ms('60s'),
    throwOnError: false,
    ...options,
  });

/**
 * React Query based custom hook for fetching the priority-ordered supplier list.
 *
 * Returns the list of prioritised suppliers together with contingent totals and
 * the current priority weight.
 *
 * @param queryParams - Recipient company ID.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook containing the priority data.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Supplier-overview/operation/get_suppliers_priority_suppliers_views_priority_get
 */
export const useQuerySuppliersPriority = <TData = SuppliersPriorityResponse>(
  recipientCompanyId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<SuppliersPriorityResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getSuppliersPriorityQueryOptions<TData>(recipientCompanyId, options),
  );
